import { useParams } from 'react-router-dom';
import useOrderBookStore from '../../../../../stores/OrderBook/orderBook.store';
import useSharedEntityStore from '../../../../../stores/SharedEntities/sharedEntities.store';
import { WithId } from '../../../../../types/Base.types';
import { useApplicationContext } from '../../../../context/ApplicationContext/ApplicationContext';
import { NO_ACTIVE_ORDER_KEY, readSnapshots, writeSnapshots } from './snapshotController.utils';
import {
  BuildSnapshotEntryParams,
  CustomSnapshot,
  CustomSnapshotByOrder,
  GetCustomSnapshotParams,
  SnapshotStorageTarget,
  UpdateSnapshotDetailsParams,
  UpdateSnapshotParams,
  UseSnapshotControllerParams,
} from './useSnapshotController.interface';

const useSnapshotController = <T>({ isOfflineMode, mode }: UseSnapshotControllerParams) => {
  const { id } = useParams<WithId>();

  const sharedEntity = useSharedEntityStore((state) => state.sharedEntity);
  const activeOrderId = useOrderBookStore((state) => state.selectedOrder?.id);
  const { team } = useApplicationContext();

  const getStorageTarget = (): SnapshotStorageTarget | undefined => {
    if (!team) {
      return;
    }

    return { teamId: team.id, sharedEntityTeamId: sharedEntity?.teamId, isOfflineMode };
  };

  const getCurrentCatalogSnapshot = (snapshots: CustomSnapshotByOrder<T>) => {
    const storedSnapshot = snapshots[activeOrderId ?? NO_ACTIVE_ORDER_KEY];

    return storedSnapshot?.id === id ? storedSnapshot : undefined;
  };

  const buildSnapshotEntry = ({
    currentSnapshot,
    name,
    search,
  }: BuildSnapshotEntryParams<T>): CustomSnapshot<T> => ({
    ...currentSnapshot,
    ...(name !== undefined && { name }),
    ...(search !== undefined && { search }),
    id,
    mode,
  });

  const updateSnapshot = ({ snapshot, name, search }: UpdateSnapshotParams<T>) => {
    const target = getStorageTarget();

    if (!target) {
      return;
    }

    const orderKey = activeOrderId ?? NO_ACTIVE_ORDER_KEY;
    const snapshots = readSnapshots<T>(target);

    writeSnapshots<T>({
      snapshots: {
        ...snapshots,
        [orderKey]: {
          ...buildSnapshotEntry({
            currentSnapshot: getCurrentCatalogSnapshot(snapshots),
            name,
            search,
          }),
          snapshot,
        },
      },
      ...target,
    });
  };

  const updateSnapshotDetails = ({ name, search }: UpdateSnapshotDetailsParams) => {
    const target = getStorageTarget();

    if (!target) {
      return;
    }

    const orderKey = activeOrderId ?? NO_ACTIVE_ORDER_KEY;
    const snapshots = readSnapshots<T>(target);

    writeSnapshots<T>({
      snapshots: {
        ...snapshots,
        [orderKey]: buildSnapshotEntry({
          currentSnapshot: getCurrentCatalogSnapshot(snapshots),
          name,
          search,
        }),
      },
      ...target,
    });
  };

  const getCustomSnapshot = ({
    shouldValidate,
  }: GetCustomSnapshotParams): CustomSnapshot<T> | undefined => {
    const target = getStorageTarget();

    if (!target) {
      return;
    }

    const snapshots = readSnapshots<T>(target);
    const orderKey = activeOrderId ?? NO_ACTIVE_ORDER_KEY;

    return [snapshots[orderKey], snapshots[NO_ACTIVE_ORDER_KEY]].find(
      (entry): entry is CustomSnapshot<T> =>
        !!entry && (!shouldValidate || (entry.id === id && entry.mode === mode)),
    );
  };

  const getActiveOrderSnapshot = (): CustomSnapshot<T> | undefined => {
    const target = getStorageTarget();

    if (!target || !activeOrderId) {
      return;
    }

    return readSnapshots<T>(target)[activeOrderId];
  };

  const getSnapshot = (): T | undefined => {
    const target = getStorageTarget();

    if (!target) {
      return;
    }

    const snapshots = readSnapshots<T>(target);
    const orderKey = activeOrderId ?? NO_ACTIVE_ORDER_KEY;

    return [snapshots[orderKey], snapshots[NO_ACTIVE_ORDER_KEY]].find(
      (entry) => entry?.id === id && entry.mode === mode && entry.snapshot,
    )?.snapshot;
  };

  const deleteSnapshot = () => {
    const target = getStorageTarget();

    if (!target) {
      return;
    }

    const { [activeOrderId ?? NO_ACTIVE_ORDER_KEY]: removedSnapshot, ...remainingSnapshots } =
      readSnapshots<T>(target);

    writeSnapshots<T>({ snapshots: remainingSnapshots, ...target });
  };

  return {
    updateSnapshot,
    updateSnapshotDetails,
    getSnapshot,
    getCustomSnapshot,
    getActiveOrderSnapshot,
    deleteSnapshot,
  };
};

export default useSnapshotController;
